export interface SankeyNode {
  id: string;
  label?: string;
  kind?: "source" | "neutral" | "income" | "expense";
}

export interface SankeyFlow {
  from: string;
  to: string;
  amount: number;
  comparison?: number;
  kind?: "source" | "neutral" | "income" | "expense";
}

export interface SankeyDocument {
  title: string;
  company?: string;
  period_label?: string;
  currency?: string;
  unit?: string;
  source_urls?: string[];
  ai_provider?: "local" | "deepseek";
  nodes?: SankeyNode[];
  flows: SankeyFlow[];
}

export interface GenerationResult {
  document: SankeyDocument;
  svg: string;
  warnings: string[];
  stats: {
    nodes: number;
    flows: number;
  };
}

/** Mirrors the server's multer limit so an oversized file never leaves the browser. */
export const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;

export type OutputLanguage = "auto" | "zh" | "en";
export type OutputUnit = "auto" | "B" | "亿";

export interface GenerationPreferences {
  language: OutputLanguage;
  unit: OutputUnit;
}

interface ApiError {
  error?: string;
  code?: string;
}

export class ApiRequestError extends Error {
  constructor(
    message: string,
    readonly code: string,
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

function visitorId(): string {
  try {
    return localStorage.getItem("product-analytics:visitor:v1") || "anonymous";
  } catch {
    return "anonymous";
  }
}

function endpoint(path: string): string {
  return `api/${path}`;
}

// Rejections that never reach the app answer with the proxy's own HTML error
// page, so there is no JSON envelope to read the code from. Without this map a
// 413 surfaced as the generic "try again later", and visitors retried the same
// oversized file dozens of times.
const STATUS_CODES: Record<number, string> = {
  413: "FILE_TOO_LARGE",
  429: "RATE_LIMITED",
};

async function parseResponse(response: Response): Promise<GenerationResult> {
  const payload = (await response.json().catch(() => null)) as
    | GenerationResult
    | ApiError
    | null;
  if (!response.ok) {
    const error = (payload ?? {}) as ApiError;
    throw new ApiRequestError(
      error.error || "生成失败，请稍后重试。",
      error.code || STATUS_CODES[response.status] || "UNKNOWN",
    );
  }
  if (!payload) throw new ApiRequestError("生成失败，请稍后重试。", "UNKNOWN");
  return payload as GenerationResult;
}

export async function loadDemo(): Promise<GenerationResult> {
  return parseResponse(await fetch(endpoint("demo")));
}

export async function generateFromCompany(
  company: string,
  period: string,
): Promise<GenerationResult> {
  return parseResponse(
    await fetch(endpoint("generate/company"), {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Visitor-Id": visitorId() },
      body: JSON.stringify({ company, period }),
    }),
  );
}

export async function generateFromUpload(
  report: File,
  preferences: GenerationPreferences,
): Promise<GenerationResult> {
  const body = new FormData();
  body.set("report", report);
  body.set("language", preferences.language);
  body.set("unit", preferences.unit);
  return parseResponse(
    await fetch(endpoint("generate/upload"), {
      method: "POST",
      headers: { "X-Visitor-Id": visitorId() },
      body,
    }),
  );
}

export async function renderPng(document: SankeyDocument): Promise<Blob> {
  const response = await fetch(endpoint("render/png"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Visitor-Id": visitorId(),
    },
    body: JSON.stringify(document),
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => ({}))) as ApiError;
    throw new ApiRequestError(
      payload.error ?? "PNG 生成失败，请稍后重试。",
      payload.code ?? "PNG_EXPORT_FAILED",
    );
  }
  return response.blob();
}
